import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { MedicalServices as MedicalIcon } from '@mui/icons-material';

const SISTEMAS = [
  { key: 'tegumentario', label: 'SISTEMA TEGUMENTARIO' },
  { key: 'digestivo', label: 'SISTEMA DIGESTIVO' },
  { key: 'respiratorio', label: 'SISTEMA RESPIRATORIO' },
  { key: 'musculoesqueletico', label: 'SISTEMA MUSCULOESQUELETICO' },
  { key: 'nervioso', label: 'SISTEMA NERVIOSO' },
  { key: 'urinario', label: 'SISTEMA URINARIO' },
  { key: 'reproductivo', label: 'SISTEMA REPRODUCTIVO' },
  { key: 'ganglios', label: 'GANGLIOS LINFATICOS' },
  { key: 'palpacion', label: 'PALPACION ABDOMINAL' },
  { key: 'cavidadOral', label: 'CAVIDAD ORAL' },
  { key: 'nariz', label: 'NARIZ' },
  { key: 'ojos', label: 'OJOS' },
  { key: 'oidos', label: 'OIDOS' }
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: '#c46494' },
    '&.Mui-focused fieldset': { borderColor: '#c46494' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#c46494' }
};

const HistoriaClinicaDialog = React.memo(function HistoriaClinicaDialog({
  open,
  onClose,
  onSave,
  editingHistoria,
  initialData,
  savingHistoria,
  selectedPatientName
}) {
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    if (!open) return;
    setFormData(initialData);
  }, [open, initialData]);

  const canSave = useMemo(() => {
    return Boolean(formData?.motivoConsulta && formData?.anamnesis && formData?.veterinario);
  }, [formData]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSistemaCheckbox = (sistemaKey, type, checked) => {
    if (type === 'normal') {
      setFormData((prev) => ({
        ...prev,
        [`${sistemaKey}Normal`]: checked,
        [`${sistemaKey}Anormal`]: checked ? false : prev[`${sistemaKey}Anormal`]
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [`${sistemaKey}Anormal`]: checked,
      [`${sistemaKey}Normal`]: checked ? false : prev[`${sistemaKey}Normal`]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: '#c46494', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <MedicalIcon />
        {editingHistoria ? 'Editar' : 'Nueva'} Historia Clinica - {selectedPatientName}
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Grid container spacing={3} sx={{ maxWidth: '1000px', width: '100%' }}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Informacion de la Consulta</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Motivo de Consulta *" value={formData.motivoConsulta} onChange={(e) => handleFieldChange('motivoConsulta', e.target.value)} multiline rows={2} required sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Anamnesis *" value={formData.anamnesis} onChange={(e) => handleFieldChange('anamnesis', e.target.value)} multiline rows={3} required sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Veterinario *" value={formData.veterinario} onChange={(e) => handleFieldChange('veterinario', e.target.value)} required sx={fieldSx} />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Antecedentes</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Enfermedades Previas" value={formData.enfermedadesPrevias} onChange={(e) => handleFieldChange('enfermedadesPrevias', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Dieta" value={formData.dieta} onChange={(e) => handleFieldChange('dieta', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Cirugias Previas" value={formData.cirugiasPrevias} onChange={(e) => handleFieldChange('cirugiasPrevias', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Esquema Vacunal" value={formData.esquemaVacunal} onChange={(e) => handleFieldChange('esquemaVacunal', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Esterilizado"
                      value={formData.esterilizado}
                      onChange={(e) => handleFieldChange('esterilizado', e.target.value)}
                      sx={fieldSx}
                    >
                      <MenuItem value="">Seleccionar</MenuItem>
                      <MenuItem value="Si">Si</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Numero de Partos" value={formData.numeroPartos} onChange={(e) => handleFieldChange('numeroPartos', e.target.value)} type="number" inputProps={{ min: 0 }} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Plan de Desparasitacion" value={formData.planDesparasitacion} onChange={(e) => handleFieldChange('planDesparasitacion', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Tratamientos Recientes" value={formData.tratamientosRecientes} onChange={(e) => handleFieldChange('tratamientosRecientes', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Informacion Social</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Viajes Recientes" value={formData.viajesRecientes} onChange={(e) => handleFieldChange('viajesRecientes', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Vive con Otras Mascotas" value={formData.viveMascotas} onChange={(e) => handleFieldChange('viveMascotas', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Comportamiento Animal" value={formData.comportamientoAnimal} onChange={(e) => handleFieldChange('comportamientoAnimal', e.target.value)} multiline rows={2} sx={fieldSx} /></Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Examen Fisico</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Estado de Conciencia" value={formData.estadoConciencia} onChange={(e) => handleFieldChange('estadoConciencia', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Estado Corporal" value={formData.estadoCorporal} onChange={(e) => handleFieldChange('estadoCorporal', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Peso (kg)" value={formData.peso} onChange={(e) => handleFieldChange('peso', e.target.value)} type="number" inputProps={{ step: '0.1' }} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="F.C. (lpm)" value={formData.frecuenciaCardiaca} onChange={(e) => handleFieldChange('frecuenciaCardiaca', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="F.R. (rpm)" value={formData.frecuenciaRespiratoria} onChange={(e) => handleFieldChange('frecuenciaRespiratoria', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="T (C)" value={formData.temperatura} onChange={(e) => handleFieldChange('temperatura', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Deshidratacion (%)" value={formData.deshidratacion} onChange={(e) => handleFieldChange('deshidratacion', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="Mucosas" value={formData.mucosas} onChange={(e) => handleFieldChange('mucosas', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="TLLC (seg)" value={formData.tllc} onChange={(e) => handleFieldChange('tllc', e.target.value)} sx={fieldSx} /></Grid>
                  <Grid item xs={12} md={4}><TextField fullWidth label="TRPC (seg)" value={formData.trpc} onChange={(e) => handleFieldChange('trpc', e.target.value)} sx={fieldSx} /></Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                <Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Evaluacion por Sistemas</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {SISTEMAS.map((sistema) => (
                    <Box key={sistema.key} sx={{ width: '100%', maxWidth: '900px', mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 2, bgcolor: 'white' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}><Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', textAlign: 'center', fontSize: '0.9rem' }}>{sistema.label}</Typography></Grid>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
                            <FormControlLabel
                              control={<Checkbox checked={formData[`${sistema.key}Normal`]} onChange={(e) => handleSistemaCheckbox(sistema.key, 'normal', e.target.checked)} sx={{ color: '#c46494', '&.Mui-checked': { color: '#c46494' } }} />}
                              label="Normal"
                              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                            />
                            <FormControlLabel
                              control={<Checkbox checked={formData[`${sistema.key}Anormal`]} onChange={(e) => handleSistemaCheckbox(sistema.key, 'anormal', e.target.checked)} sx={{ color: '#c46494', '&.Mui-checked': { color: '#c46494' } }} />}
                              label="Anormal"
                              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Evaluacion"
                            value={formData[`${sistema.key}Evaluacion`]}
                            onChange={(e) => handleFieldChange(`${sistema.key}Evaluacion`, e.target.value)}
                            multiline
                            rows={1}
                            sx={fieldSx}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Examenes Complementarios</Typography><TextField fullWidth label="Examenes Complementarios" value={formData.ayudasDiagnosticas} onChange={(e) => handleFieldChange('ayudasDiagnosticas', e.target.value)} multiline rows={3} sx={fieldSx} /></Card></Grid>
            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Tratamiento en Casa</Typography><TextField fullWidth label="Tratamiento en Casa" value={formData.tratamientoEnCasa} onChange={(e) => handleFieldChange('tratamientoEnCasa', e.target.value)} multiline rows={3} sx={fieldSx} /></Card></Grid>
            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Formula Medica</Typography><TextField fullWidth label="Formula Medica" value={formData.formulaMedica} onChange={(e) => handleFieldChange('formulaMedica', e.target.value)} multiline rows={4} sx={fieldSx} /></Card></Grid>
            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Diagnostico</Typography><TextField fullWidth label="Diagnostico" value={formData.diagnostico} onChange={(e) => handleFieldChange('diagnostico', e.target.value)} multiline rows={3} sx={fieldSx} /></Card></Grid>
            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Pronostico</Typography><TextField fullWidth label="Pronostico" value={formData.pronostico} onChange={(e) => handleFieldChange('pronostico', e.target.value)} multiline rows={3} sx={fieldSx} /></Card></Grid>
            <Grid item xs={12}><Card sx={{ p: 3, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}><Typography variant="h6" sx={{ color: '#c46494', mb: 3, textAlign: 'center' }}>Observaciones</Typography><TextField fullWidth label="Observaciones Adicionales" value={formData.observaciones} onChange={(e) => handleFieldChange('observaciones', e.target.value)} multiline rows={4} sx={fieldSx} /></Card></Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#f8f9fa' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: '#666', color: '#666' }}>
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(formData)}
          variant="contained"
          disabled={savingHistoria || !canSave}
          startIcon={savingHistoria ? <CircularProgress size={20} color="inherit" /> : <MedicalIcon />}
          sx={{ bgcolor: '#c46494', minWidth: 180, '&:hover': { bgcolor: '#a05080' }, '&:disabled': { bgcolor: '#e0e0e0' } }}
        >
          {savingHistoria ? 'Guardando...' : (editingHistoria ? 'Actualizar Historia Clinica' : 'Guardar Historia Clinica')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default HistoriaClinicaDialog;
